const WEEKDAYS: Record<string, string[]> = {
  "pt-BR": ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"],
  en: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
};
const MONTHS: Record<string, string[]> = {
  "pt-BR": ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"],
  en: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
};
const TZ = "America/Sao_Paulo";

function partsInSaoPaulo(date: Date) {
  const f = new Intl.DateTimeFormat("en-US", {
    timeZone: TZ, year: "numeric", month: "numeric", day: "numeric",
    hour: "numeric", minute: "numeric", hourCycle: "h23",
  });
  const p = Object.fromEntries(f.formatToParts(date).map((x) => [x.type, x.value]));
  return { year: +p.year, month: +p.month, day: +p.day, hour: +p.hour, minute: +p.minute };
}

/** `qui, 24 abr` for a timestamp or a `YYYY-MM-DD` calendar day. */
export function formatDay(value: string | Date, language = "pt-BR"): string {
  const locale = language.startsWith("en") ? "en" : "pt-BR";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split("-").map(Number);
    const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
    return locale === "en" ? `${WEEKDAYS[locale][dow]}, ${MONTHS[locale][m - 1]} ${d}` : `${WEEKDAYS[locale][dow]}, ${d} ${MONTHS[locale][m - 1]}`;
  }
  const date = typeof value === "string" ? new Date(value) : value;
  const p = partsInSaoPaulo(date);
  const dow = new Date(Date.UTC(p.year, p.month - 1, p.day)).getUTCDay();
  return locale === "en" ? `${WEEKDAYS[locale][dow]}, ${MONTHS[locale][p.month - 1]} ${p.day}` : `${WEEKDAYS[locale][dow]}, ${p.day} ${MONTHS[locale][p.month - 1]}`;
}

/** `14h32` (24h, America/Sao_Paulo). */
export function formatTime(value: string | Date, language = "pt-BR"): string {
  const p = partsInSaoPaulo(typeof value === "string" ? new Date(value) : value);
  if (language.startsWith("en")) {
    const hour = p.hour % 12 || 12;
    return `${hour}:${String(p.minute).padStart(2, "0")} ${p.hour < 12 ? "AM" : "PM"}`;
  }
  return `${p.hour}h${String(p.minute).padStart(2, "0")}`;
}

export const formatDateTime = (value: string | Date, language = "pt-BR"): string => `${formatDay(value, language)}, ${formatTime(value, language)}`;

export function formatRelativeDateTime(value: string | Date, language = "pt-BR"): string {
  const locale = language.startsWith("en") ? "en" : "pt-BR";
  const delta = (typeof value === "string" ? new Date(value) : value).getTime() - Date.now();
  const absolute = Math.abs(delta);
  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ["second", 1_000], ["minute", 60_000], ["hour", 3_600_000], ["day", 86_400_000],
    ["month", 2_592_000_000], ["year", 31_536_000_000],
  ];
  let unit: Intl.RelativeTimeFormatUnit = "year";
  let size = 31_536_000_000;
  for (const candidate of units) {
    unit = candidate[0];
    size = candidate[1];
    if (absolute < size * (candidate[0] === "second" ? 60 : candidate[0] === "minute" ? 60 : candidate[0] === "hour" ? 24 : candidate[0] === "day" ? 30 : 12)) break;
  }
  const count = Math.round(delta / size);
  return new Intl.RelativeTimeFormat(locale, { numeric: "auto" }).format(count, unit);
}
